<script setup lang="ts">
import { computed, ref } from "vue";
import { useI18n } from "vue-i18n";

import { useDefaultToast } from "@/composables/useDefaultToast";
import { getCancelReasonsOptions } from "@/helpers/mission";
import { useAuthStore } from "@/modules/auth/store/authStore";
import missionsService from "@/modules/missions/services/missionsService";
import { MissionCommand } from '@/types/mission';

import type { SelectOption } from "@/dsComponents/inputs/types";
import type { LabelValue } from '@/types/common';

import ButtonAlt from "@/dsComponents/buttons/ButtonAlt.vue";
import Select from "@/dsComponents/inputs/Select.vue";
import TextareaInput from "@/dsComponents/inputs/TextareaInput.vue";
import Modal from "@/dsComponents/modals/Modal.vue";

interface CancelMissionProps {
    open: boolean;
    missionId: string;
}

const props = defineProps<CancelMissionProps>();

const emit = defineEmits<{
    close: [];
    cancel: [missionId: string];
}>();

const { t } = useI18n();
const authStore = useAuthStore();
const { toastSuccess, toastError } = useDefaultToast();

const isLoading = ref(false);
const selectedReason = ref<LabelValue>();
const reasonComplement = ref("");

const cancelReasonsOptions = computed(() =>
    getCancelReasonsOptions(Boolean(authStore.isAgency)).map((option) => ({
        ...option,
        label: t(option.label),
    })),
);

const selectedReasonValue = computed({
    get: () => selectedReason.value?.value || "",
    set: (value: string) => {
        selectedReason.value = value
            ? cancelReasonsOptions.value.find((reason) => reason.value === value)
            : undefined;
    },
});

const isOtherReasonSelected = computed(() => selectedReason.value?.value === "other");

const resetForm = () => {
    reasonComplement.value = "";
    selectedReason.value = undefined;
};

const handleReasonSelect = (option: SelectOption) => {
    selectedReason.value = {
        label: option.label ?? "",
        value: option.value,
    };
};

const handleClose = () => {
    resetForm();
    emit("close");
};

const handleConfirm = async () => {
    try {
        isLoading.value = true;
        await missionsService.sendCommandMission({
            missionId: props.missionId,
            command: MissionCommand.cancel,
            data: {
                cancelReason: selectedReason.value?.value,
                ...(isOtherReasonSelected.value && { cancelReasonOther: reasonComplement.value }),
            },
        });

        resetForm();
        toastSuccess(t("modals.cancelMission.success"));
        emit("cancel", props.missionId);
        emit("close");
    } catch (error) {
        console.error("Error cancelling mission:", error);
        toastError(t("modals.cancelMission.error"));
    } finally {
        isLoading.value = false;
    }
};
</script>

<template>
    <Modal :open="open" :title="t('modals.cancelMission.title')" width="500px" @close="handleClose">
        <template #default>
            <div class="cancel-mission">
                <span class="cancel-mission__description typo-sm-medium">{{ t("modals.cancelMission.description") }}</span>
                <Select
                    v-model="selectedReasonValue"
                    :options="cancelReasonsOptions"
                    :label="t('form.reason')"
                    width="100%"
                    @select="handleReasonSelect"
                />
                <TextareaInput
                    v-if="isOtherReasonSelected"
                    v-model="reasonComplement"
                    size="md"
                    label=""
                    :rows="3"
                    auto-resize
                />
            </div>
        </template>
        <template #footer>
            <div class="cancel-mission__actions">
                <ButtonAlt type="neutral" size="sm" variant="ghost" :label="t('global.actions.cancel')" :loading="isLoading" @click="handleClose" />
                <ButtonAlt
                    type="error"
                    variant="filled"
                    :disabled="!selectedReason?.value"
                    :label="t('global.actions.confirm')"
                    :loading="isLoading"
                    @click="handleConfirm"
                />
            </div>
        </template>
    </Modal>
</template>

<style scoped lang="scss">
.cancel-mission {
    display: flex;
    flex-direction: column;
    gap: var(--spacing-xl, 16px);

    &__description {
        color: var(--text-main-900, #00061a);
    }

    &__actions {
        display: flex;
        align-items: center;
        justify-content: flex-end;
        gap: var(--spacing-lg, 12px);
    }
}
</style>
